import { useState, useEffect, useCallback, useRef } from 'react';
import type { PostgrestError } from '@supabase/supabase-js';

export function useSupabaseQuery<T>(
  queryFn: () => Promise<{ data: T | null; error: PostgrestError | null }>,
  deps: React.DependencyList = []
): { data: T | null; loading: boolean; error: string | null; refetch: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const counterRef = useRef(0);

  const fetch = useCallback(async () => {
    const id = ++counterRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await queryFn();
      if (id !== counterRef.current) return;
      if (result.error) {
        setError(result.error.message);
        setData(null);
      } else {
        setData(result.data);
      }
    } catch (err) {
      if (id !== counterRef.current) return;
      setError(err instanceof Error ? err.message : 'Unknown error');
      setData(null);
    } finally {
      if (id === counterRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}
